import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Plus, Edit2, Trash2, Download, Upload, CheckCircle, 
  XCircle, HelpCircle, AlertCircle, RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminQuestions = () => {
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [migrateLoading, setMigrateLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    question: '',
    options: [
      { id: 'a', text: '' },
      { id: 'b', text: '' },
      { id: 'c', text: '' },
      { id: 'd', text: '' }
    ],
    correct_answer: ''
  });

  // Fetch courses with question counts
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API}/admin/questions/courses-with-questions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCourses(response.data);
      } catch (error) {
        toast.error('Erro ao carregar cursos');
      }
    };
    if (token) fetchCourses();
  }, [token]);

  // Fetch questions when course is selected
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedCourse) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API}/admin/questions/course/${selectedCourse}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuestions(response.data.questions);
      } catch (error) {
        toast.error('Erro ao carregar questões');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [selectedCourse, token]);

  const handleMigrate = async () => {
    if (!window.confirm('Deseja migrar as questões do arquivo para o banco de dados?')) return;
    
    setMigrateLoading(true);
    try {
      const response = await axios.post(`${API}/admin/questions/migrate-from-file/`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      // Refresh courses list
      const coursesResponse = await axios.get(`${API}/admin/questions/courses-with-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(coursesResponse.data);
      // Refresh questions if a course is selected
      if (selectedCourse) {
        const questionsResponse = await axios.get(`${API}/admin/questions/course/${selectedCourse}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuestions(questionsResponse.data.questions);
      }
    } catch (error) {
      toast.error('Erro ao migrar questões');
    } finally {
      setMigrateLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingQuestion(null);
    setFormData({
      question: '',
      options: [
        { id: 'a', text: '' },
        { id: 'b', text: '' },
        { id: 'c', text: '' },
        { id: 'd', text: '' }
      ],
      correct_answer: ''
    });
    setShowModal(true);
  };

  const openEditModal = (q) => {
    setEditingQuestion(q);
    setFormData({
      question: q.question,
      options: q.options.length === 4 ? q.options : [
        { id: 'a', text: q.options[0]?.text || '' },
        { id: 'b', text: q.options[1]?.text || '' },
        { id: 'c', text: q.options[2]?.text || '' },
        { id: 'd', text: q.options[3]?.text || '' }
      ],
      correct_answer: q.correct_answer
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.question.trim()) {
      toast.error('Digite a pergunta');
      return;
    }
    
    const filledOptions = formData.options.filter(opt => opt.text.trim());
    if (filledOptions.length < 2) {
      toast.error('Preencha pelo menos 2 opções');
      return;
    }
    
    if (!formData.correct_answer) {
      toast.error('Selecione a resposta correta');
      return;
    }

    try {
      const payload = {
        course_id: selectedCourse,
        question: formData.question.trim(),
        options: formData.options.filter(opt => opt.text.trim()),
        correct_answer: formData.correct_answer
      };

      if (editingQuestion) {
        // Update
        await axios.put(`${API}/admin/questions/${editingQuestion.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Questão atualizada!');
      } else {
        // Create - note the trailing slash
        await axios.post(`${API}/admin/questions/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Questão criada!');
      }

      setShowModal(false);
      
      // Refresh questions
      const response = await axios.get(`${API}/admin/questions/course/${selectedCourse}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions);
      
      // Refresh course counts
      const coursesResponse = await axios.get(`${API}/admin/questions/courses-with-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(coursesResponse.data);
    } catch (error) {
      toast.error('Erro ao salvar questão');
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta questão?')) return;

    try {
      await axios.delete(`${API}/admin/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Questão excluída!');
      setQuestions(questions.filter(q => q.id !== questionId));
      
      // Refresh course counts
      const coursesResponse = await axios.get(`${API}/admin/questions/courses-with-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(coursesResponse.data);
    } catch (error) {
      toast.error('Erro ao excluir questão');
    }
  };

  const handleToggleActive = async (question) => {
    try {
      await axios.put(`${API}/admin/questions/${question.id}`, {
        is_active: !question.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuestions(questions.map(q => 
        q.id === question.id ? { ...q, is_active: !q.is_active } : q
      ));
      
      toast.success(question.is_active ? 'Questão desativada' : 'Questão ativada');
    } catch (error) {
      toast.error('Erro ao atualizar questão');
    }
  };

  const getSelectedCourseTitle = () => {
    const course = courses.find(c => c.course_id === selectedCourse);
    return course?.title || 'Curso';
  };

  return (
    <div className="space-y-6">
      {/* Header with Migration Button */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-xl font-semibold text-white">Questões de Avaliação</h2>
          <p className="text-sm text-gray-400">Gerencie as questões de prova de cada curso</p>
        </div>
        <Button
          onClick={handleMigrate}
          variant="outline"
          className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
          disabled={migrateLoading}
          data-testid="migrate-questions-btn"
        >
          {migrateLoading ? (
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          Migrar do Arquivo
        </Button>
      </div>

      {/* Course Selection */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white text-lg">Selecionar Curso</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((course) => (
              <div
                key={course.course_id}
                onClick={() => setSelectedCourse(course.course_id)}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedCourse === course.course_id
                    ? 'border-red-500 bg-red-500/10'
                    : 'border-zinc-700 hover:border-zinc-600'
                }`}
                data-testid={`course-select-${course.course_id}`}
              >
                <h3 className="text-white font-medium mb-2 line-clamp-2">{course.title}</h3>
                <div className="flex items-center gap-2">
                  <Badge variant={course.total_questions > 0 ? 'default' : 'secondary'}>
                    <HelpCircle className="mr-1 h-3 w-3" />
                    {course.total_questions} questões
                  </Badge>
                  {course.active_questions !== course.total_questions && (
                    <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                      {course.active_questions} ativas
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Questions List */}
      {selectedCourse && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white text-lg">{getSelectedCourseTitle()}</CardTitle>
              <p className="text-sm text-gray-400">{questions.length} questões cadastradas</p>
            </div>
            <Button
              onClick={openCreateModal}
              className="bg-red-600 hover:bg-red-700"
              data-testid="add-question-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nova Questão
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-400">Carregando...</div>
            ) : questions.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                <p className="text-gray-400">Nenhuma questão cadastrada para este curso</p>
                <p className="text-sm text-gray-500 mt-2">
                  Clique em "Nova Questão" ou "Migrar do Arquivo" para começar
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {questions.map((q, index) => (
                  <div
                    key={q.id}
                    className={`p-4 rounded-lg border ${
                      q.is_active ? 'border-zinc-700' : 'border-zinc-800 opacity-60'
                    }`}
                    data-testid={`question-item-${q.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-gray-400">
                            #{q.question_number}
                          </Badge>
                          {!q.is_active && (
                            <Badge variant="secondary" className="text-yellow-500">
                              Inativa
                            </Badge>
                          )}
                        </div>
                        <p className="text-white font-medium mb-3">{q.question}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {q.options.map((opt) => (
                            <div
                              key={opt.id}
                              className={`text-sm px-3 py-2 rounded ${
                                opt.id === q.correct_answer
                                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                  : 'bg-zinc-800 text-gray-300'
                              }`}
                            >
                              <span className="font-medium mr-2">{opt.id.toUpperCase()})</span>
                              {opt.text}
                              {opt.id === q.correct_answer && (
                                <CheckCircle className="inline ml-2 h-4 w-4" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(q)}
                          className="border-zinc-700"
                          data-testid={`edit-question-${q.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleActive(q)}
                          className={q.is_active ? 'border-yellow-500 text-yellow-500' : 'border-green-500 text-green-500'}
                          data-testid={`toggle-question-${q.id}`}
                        >
                          {q.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(q.id)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          data-testid={`delete-question-${q.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingQuestion ? 'Editar Questão' : 'Nova Questão'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Question Text */}
            <div>
              <Label className="text-gray-300">Pergunta</Label>
              <Textarea
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Digite a pergunta..."
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                rows={3}
                data-testid="question-input"
              />
            </div>

            {/* Options */}
            <div>
              <Label className="text-gray-300">Opções</Label>
              <div className="space-y-2 mt-1">
                {formData.options.map((opt, index) => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      formData.correct_answer === opt.id
                        ? 'bg-green-500 text-white'
                        : 'bg-zinc-800 text-gray-400'
                    }`}>
                      {opt.id.toUpperCase()}
                    </div>
                    <Input
                      value={opt.text}
                      onChange={(e) => {
                        const newOptions = [...formData.options];
                        newOptions[index].text = e.target.value;
                        setFormData({ ...formData, options: newOptions });
                      }}
                      placeholder={`Opção ${opt.id.toUpperCase()}`}
                      className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      data-testid={`option-${opt.id}-input`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant={formData.correct_answer === opt.id ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, correct_answer: opt.id })}
                      className={formData.correct_answer === opt.id 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'border-zinc-700 text-gray-400'
                      }
                      data-testid={`correct-answer-${opt.id}`}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Clique no botão verde para marcar a resposta correta
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              className="border-zinc-700 text-gray-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-question-btn"
            >
              {editingQuestion ? 'Salvar Alterações' : 'Criar Questão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestions;
